import { RightOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { Button, Card, Col, Drawer, message, Row, Space, Tag, Typography } from 'antd';
import React, { useMemo } from 'react';
import { setConsoleCtx, deriveConsoleCtx } from '@/utils/consoleCtx';
import { openTokenPlatformConsole } from '@/utils/tokenPlatformSso';
import { PRODUCTS } from '@/pages/console/products';

const { Title, Paragraph } = Typography;

interface ProductSwitcherProps {
  /** 是否展开抽屉 */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
}

/**
 * 产品切换抽屉。
 *
 * 在产品页内点击侧栏首项「产品中心」时唤起（见 app.tsx menuItemRender），
 * 展示「当前产品」以外的其余产品卡片入口；点击卡片切换产品上下文并跳转，
 * 不离开当前页面去 /console —— 满足「在该产品页面展示出另外三个产品的卡片入口」。
 */
const ProductSwitcher: React.FC<ProductSwitcherProps> = ({ open, onClose }) => {
  // 当前产品上下文：路径推导优先（WIS-369，直接进入产品页 sessionStorage 为空时也能识别），
  // 再回退 sessionStorage。控制台首页 ctx 为空时展示全部产品。
  const currentCtx = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return (
      deriveConsoleCtx(window.location.pathname) ||
      window.sessionStorage.getItem('wischoicer_console_ctx') ||
      ''
    );
  }, [open]);

  const otherProducts = useMemo(() => {
    if (!currentCtx) return PRODUCTS;
    return PRODUCTS.filter((p) => p.ctx !== currentCtx);
  }, [currentCtx]);

  const handleEnter = async (product: (typeof PRODUCTS)[number]) => {
    // WIS-369 opt4：Token 平台免密直跳 new-api（不在站内切换）
    if (product.sso) {
      const r = await openTokenPlatformConsole('/dashboard');
      if (!r.ok) message.error(r.message || '获取控制台链接失败');
      return;
    }
    setConsoleCtx(product.ctx);
    history.push(product.path);
    onClose();
  };

  return (
    <Drawer
      title="切换产品"
      placement="right"
      width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth : 560)}
      open={open}
      onClose={onClose}
      styles={{ body: { padding: 16 } }}
    >
      <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 16 }}>
        选择要进入的产品工作台。
      </Paragraph>
      <Row gutter={[16, 16]}>
        {otherProducts.map((product) => (
          <Col xs={24} sm={12} key={product.key}>
            <Card
              hoverable
              style={{ height: '100%' }}
              styles={{
                body: {
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: `${product.color}10`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    color: product.color,
                  }}
                >
                  {product.icon}
                </div>
                <Title level={5} style={{ margin: 0 }}>
                  {product.title}
                </Title>
              </div>

              <Paragraph type="secondary" style={{ flex: 1, marginBottom: 12 }}>
                {product.description}
              </Paragraph>

              <div style={{ marginBottom: 12 }}>
                <Space size={[4, 4]} wrap>
                  {product.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </Space>
              </div>

              <Button
                block
                icon={<RightOutlined />}
                onClick={() => handleEnter(product)}
                style={{
                  background: product.color,
                  borderColor: product.color,
                  color: '#fff',
                }}
              >
                进入工作台
              </Button>
            </Card>
          </Col>
        ))}
      </Row>
    </Drawer>
  );
};

export default ProductSwitcher;
